import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismCheckbox,
  PrismNumberInput,
  PrismTooltip,
  styleEntry,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import {
  COLOR_PMS_ROOM_SELECTED,
  getCssVariableValue,
} from 'frontend-utils/utils/css-variable';
import {
  NUMBER_INPUT_MAX_VALUE,
  PMS_SETTINGS_OFFSET_TYPES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import safeMultiply from 'lumen/utils/numeric/safe-multiply';
import { findNodeByRoomName } from 'lumen/utils/pricing-assistant/pms-settings-find-node-by-room-name';
import { timeout } from 'lumen/utils/waiters';

interface SettingsPmsIntegrationRoomtypeOffsetSignature {
  Args: {
    channelRoomNameToColorMapping: unknown;
    localSettingsCopy: unknown;
    offsetUnit: string;
    pmsRoomName: unknown;
    pmsRoomNode: unknown;
    pmsRoomtypeHierarchyLeadingRoomChildren: unknown;
    updateLocalSettingsCopy: unknown;
  };
}

export default class SettingsPmsIntegrationRoomtypeOffset extends Component<SettingsPmsIntegrationRoomtypeOffsetSignature> {
  @service declare access: Services['access'];

  debouncedUpdateLocalCopyOnOffsetChangeTask = task(
    { restartable: true },
    async (offsetValue) => {
      await timeout();

      // Update localSettingsCopy
      const newSettingsObject = this.args.localSettingsCopy;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const node = findNodeByRoomName(
        this.args.pmsRoomName,
        // @ts-expect-error: Incorrect type
        newSettingsObject.pms_roomtype_hierarchy,
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      node.offsets[0].value = offsetValue;
      // @ts-expect-error: Incorrect type
      this.args.updateLocalSettingsCopy(newSettingsObject);
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get bookingRoomColor() {
    // Show a grey dot for rooms that are not selected
    if (!this.isPmsRoomSelected) {
      return getCssVariableValue(COLOR_PMS_ROOM_SELECTED);
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.channelRoomNameToColorMapping[this.bookingRoomName];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get bookingRoomName() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.localSettingsCopy.pms_id_to_booking_room_name_mapping[
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.pmsRoomName
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedOffsetValue() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const offsetType = this.offsetObject.type;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    let offsetValue = this.offsetObject.value;

    if (
      offsetValue !== null &&
      offsetType === PMS_SETTINGS_OFFSET_TYPES.RELATIVE
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      offsetValue = safeMultiply(offsetValue, 100);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return offsetValue;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isOffsetInputDisabled() {
    return (
      this.access.isPricingAssistantPmsSettingsActionOrInputDisabled ||
      !this.isPmsRoomSelected
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPmsRoomSelected() {
    // The included primitive type property update is not picked up by the parent each-in node variable binding. As a result, simply returning this.args.pmsRoomNode.included doesn't re-run this getter when included property is changed.
    // Therefore, resorting to explicitly getting the included property value from the pmsRoomtypeHierarchyLeadingRoomChildren object.
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.pmsRoomtypeHierarchyLeadingRoomChildren[
      this.args.pmsRoomName
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    ].included;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get offsetObject() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.pmsRoomNode.offsets[0];
  }

  @action
  // @ts-expect-error: Incorrect type
  updateOffsetValue(value): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    let offsetValue = value;

    if (value <= NUMBER_INPUT_MAX_VALUE) {
      if (
        offsetValue !== null &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.offsetObject.type === PMS_SETTINGS_OFFSET_TYPES.RELATIVE
      ) {
        offsetValue /= 100;
      }

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.debouncedUpdateLocalCopyOnOffsetChangeTask.perform(offsetValue);
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  updatePmsRoomSelection(event): void {
    // Update localSettingsCopy
    const newSettingsObject = this.args.localSettingsCopy;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const node = findNodeByRoomName(
      this.args.pmsRoomName,
      // @ts-expect-error: Incorrect type
      newSettingsObject.pms_roomtype_hierarchy,
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    node.included = event.target.checked;
    // @ts-expect-error: Incorrect type
    this.args.updateLocalSettingsCopy(newSettingsObject);
  }

  <template>
    <div class="pms-roomtype-mapping-row flex flex-row items-center">
      <div
        class="pms-roomtype-mapping-row__pms-room flex flex-row items-center"
      >
        <PrismCheckbox
          @checked={{this.isPmsRoomSelected}}
          @disabled={{this.access.isPricingAssistantPmsSettingsActionOrInputDisabled}}
          @onChange={{this.updatePmsRoomSelection}}
        />
        <div
          class="identifier-bullet ml-200"
          style={{styleEntry "background-color" this.bookingRoomColor}}
        >
        </div>

        <p class="text-300--regular truncate">
          {{! @glint-expect-error: Incorrect type }}
          {{@pmsRoomName}}
          {{! @glint-expect-error: Incorrect type }}
          <PrismTooltip @side="top" @text={{@pmsRoomName}} />
        </p>
      </div>

      <div class="pms-roomtype-mapping-row__offset">
        <PrismNumberInput
          @disabled={{this.isOffsetInputDisabled}}
          @inlineLabel={{@offsetUnit}}
          @isTouched={{true}}
          @max={{NUMBER_INPUT_MAX_VALUE}}
          @onValueChange={{this.updateOffsetValue}}
          @removePadding={{true}}
          @required={{true}}
          @value={{this.formattedOffsetValue}}
        />
      </div>
    </div>
  </template>
}
